import CommentIcon from '@mui/icons-material/Comment';
import FilterListIcon from '@mui/icons-material/FilterList';
import MessageIcon from '@mui/icons-material/Message';
import ThumbUpIcon from '@mui/icons-material/ThumbUp';
import {
  IconButton,
  Menu,
  MenuItem,
  TableCell,
  TableHead,
  TableRow,
  TableSortLabel,
  Typography
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  backgroundColor: theme.palette.primary.main,
  color: theme.palette.common.white,
  fontWeight: 'bold',
  padding: theme.spacing(1.5),
  whiteSpace: 'nowrap',
  '&:hover': {
    backgroundColor: theme.palette.primary.dark,
  },
}));

const MessagePostGridHead = ({ handleRequestSort, handleTypeFilter, order, orderBy, serviceName, selectedType }) => {
  const { t: translate } = useTranslation();
  const [anchorEl, setAnchorEl] = useState(null);

  const handleFilterClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleFilterClose = () => {
    setAnchorEl(null);
  };

  const handleTypeSelect = (type) => {
    handleTypeFilter(type === 'all' ? '' : type);
    handleFilterClose();
  };

  const getTypeOptions = () => {
    if (serviceName === 'instagram') {
      return [
        {
          value: 'message',
          label: translate('crm.message', 'Message'),
          icon: <MessageIcon fontSize="small" sx={{ color: 'primary.main' }} />,
        },
        {
          value: 'comment',
          label: translate('crm.comment', 'Comment'),
          icon: <CommentIcon fontSize="small" sx={{ color: 'primary.main' }} />,
        },
      ];
    } else if (serviceName === 'facebook') {
      return [
        {
          value: 'message',
          label: translate('crm.message', 'Message'),
          icon: <MessageIcon fontSize="small" sx={{ color: 'primary.main' }} />,
        },
        {
          value: 'comment',
          label: translate('crm.comment', 'Comment'),
          icon: <CommentIcon fontSize="small" sx={{ color: 'primary.main' }} />,
        },
        {
          value: 'like',
          label: translate('crm.like', 'Like'),
          icon: <ThumbUpIcon fontSize="small" sx={{ color: 'primary.main' }} />,
        },
      ];
    }
    return [];
  };

  const getTableColumns = () => {
    if (serviceName === 'instagram') {
      if (selectedType === 'message') {
        return [
          { id: 'created_time', label: translate('crm.created', 'Created'), hasSort: true },
          { id: 'type', label: translate('crm.type', 'Type'), hasSort: false, hasFilter: true },
          { id: 'sender_name', label: translate('crm.sender_name', 'Sender Name'), hasSort: false },
          { id: 'message', label: translate('crm.message', 'Message'), hasSort: false },
          { id: 'profile_link', label: translate('crm.profile_link', 'Profile Link'), hasSort: false },
          { id: 'status', label: translate('crm.status', 'Status'), hasSort: false },
        ];
      } else if (selectedType === 'comment') {
        return [
          { id: 'created_time', label: translate('crm.created', 'Created'), hasSort: true },
          { id: 'type', label: translate('crm.type', 'Type'), hasSort: false, hasFilter: true },
          { id: 'from_username', label: translate('crm.from_name', 'From Name'), hasSort: false },
          { id: 'message', label: translate('crm.message', 'Message'), hasSort: false },
          { id: 'profile_link', label: translate('crm.profile_link', 'Profile Link'), hasSort: false },
          { id: 'post_link', label: translate('crm.post_link', 'Post Link'), hasSort: false },
          { id: 'status', label: translate('crm.status', 'Status'), hasSort: false },
        ];
      }
    } else if (serviceName === 'facebook') {
      if (selectedType === 'message') {
        return [
          { id: 'created_time', label: translate('crm.created', 'Created'), hasSort: true },
          { id: 'type', label: translate('crm.type', 'Type'), hasSort: false, hasFilter: true },
          { id: 'sender_name', label: translate('crm.from', 'From'), hasSort: false },
          { id: 'message', label: translate('crm.message', 'Message'), hasSort: false },
          { id: 'status', label: translate('crm.status', 'Status'), hasSort: false },
        ];
      } else if (selectedType === 'comment') {
        return [
          { id: 'created_time', label: translate('crm.created', 'Created'), hasSort: true },
          { id: 'type', label: translate('crm.type', 'Type'), hasSort: false, hasFilter: true },
          { id: 'from_name', label: translate('crm.from', 'From'), hasSort: false },
          { id: 'message', label: translate('crm.message', 'Message'), hasSort: false },
          { id: 'link', label: translate('crm.post', 'Post'), hasSort: false },
          { id: 'status', label: translate('crm.status', 'Status'), hasSort: false },
        ];
      } else if (selectedType === 'like') {
        return [
          { id: 'created_time', label: translate('crm.created', 'Created'), hasSort: true },
          { id: 'type', label: translate('crm.type', 'Type'), hasSort: false, hasFilter: true },
          { id: 'from_name', label: translate('crm.from_name', 'From Name'), hasSort: false },
          { id: 'verb', label: translate('crm.action', 'Action'), hasSort: false },
          { id: 'link', label: translate('crm.post', 'Post'), hasSort: false },
          { id: 'status', label: translate('crm.status', 'Status'), hasSort: false },
        ];
      }
    }
    return [];
  };

  const columns = getTableColumns();

  return (
    <TableHead>
      <TableRow>
        {columns.map((headCell) => (
          <StyledTableCell key={headCell.id} align="left">
            {headCell.hasSort ? (
              <TableSortLabel
                active={orderBy === headCell.id}
                direction={orderBy === headCell.id ? order : 'asc'}
                onClick={() => handleRequestSort(headCell.id)}
              >
                {headCell.label}
              </TableSortLabel>
            ) : headCell.hasFilter ? (
              <>
                {headCell.label}
                <IconButton
                  size="small"
                  onClick={handleFilterClick}
                  sx={{
                    color: 'white', // Bright white color
                    '&:hover': {
                      backgroundColor: 'rgba(255, 255, 255, 0.1)', // Subtle background on hover
                      color: '#f5f5f5', // Slightly off-white for hover effect
                    },
                  }}
                >
                  <FilterListIcon fontSize="small" />
                </IconButton>
                <Menu
                  anchorEl={anchorEl}
                  open={Boolean(anchorEl)}
                  onClose={handleFilterClose}
                  anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'left',
                  }}
                >
                  {getTypeOptions().map((option) => (
                    <MenuItem
                      key={option.value}
                      onClick={() => handleTypeSelect(option.value)}
                      selected={option.value === selectedType}
                      sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
                    >
                      {option.icon}
                      <Typography variant="body2">{option.label}</Typography>
                    </MenuItem>
                  ))}
                </Menu>
              </>
            ) : (
              headCell.label
            )}
          </StyledTableCell>
        ))}
      </TableRow>
    </TableHead>
  );
};

export default MessagePostGridHead;